import { Link } from "react-router-dom";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const actionCardVariants = cva(
  "group inline-flex items-center gap-4 rounded-2xl px-5 py-3.5 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-md",
        secondary:
          "border bg-card text-foreground shadow-xs hover:border-foreground/20 hover:shadow-sm",
      },
    },
    defaultVariants: { variant: "secondary" },
  },
);

const iconBubbleVariants = cva(
  "flex size-8 shrink-0 items-center justify-center rounded-xl",
  {
    variants: {
      variant: {
        primary: "bg-primary-foreground/15 text-primary-foreground",
        secondary: "bg-muted text-muted-foreground",
      },
    },
    defaultVariants: { variant: "secondary" },
  },
);

const chevronVariants = cva(
  "ml-auto h-4 w-4 transition-transform group-hover:translate-x-0.5",
  {
    variants: {
      variant: {
        primary: "text-primary-foreground",
        secondary: "text-muted-foreground",
      },
    },
    defaultVariants: { variant: "secondary" },
  },
);

export type ActionCardProps = VariantProps<typeof actionCardVariants> & {
  icon: LucideIcon;
  label: string;
  description?: string;
  rightSlot?: React.ReactNode;
  hideChevron?: boolean;
  className?: string;
  to?: string;
  onClick?: () => void;
  target?: string;
  rel?: string;
  disabled?: boolean;
  "aria-label"?: string;
};

export function ActionCard(props: ActionCardProps) {
  const {
    variant,
    icon: Icon,
    label,
    description,
    rightSlot,
    hideChevron,
    className,
  } = props;

  const body = (
    <>
      <span className={iconBubbleVariants({ variant })}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-semibold">{label}</span>
        {description && (
          <span
            className={cn(
              "mt-0.5 block truncate text-sm",
              variant === "primary"
                ? "text-primary-foreground"
                : "text-muted-foreground",
            )}
          >
            {description}
          </span>
        )}
      </span>
      {rightSlot && (
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {rightSlot}
        </span>
      )}
      {!hideChevron && (
        <ChevronRight
          className={chevronVariants({ variant })}
          aria-hidden="true"
        />
      )}
    </>
  );

  const classes = cn(actionCardVariants({ variant }), className);

  if (props.to) {
    return (
      <Link
        to={props.to}
        onClick={props.onClick}
        target={props.target}
        rel={props.rel}
        aria-label={props["aria-label"]}
        className={classes}
      >
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props["aria-label"]}
      className={classes}
    >
      {body}
    </button>
  );
}
